import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarDays, MapPin, PhoneCall } from "lucide-react";

import { fadeUp } from "../../animations/variants";

import { useSelector } from "react-redux";
import { textWorkshop } from "data/workshops";
import CTAButton from "Util/CTAButton";

const LatestWorkshop = () => {
  const lang = useSelector((state) => state.language.lang);

  const t = textWorkshop[lang];

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.eventbrite.com/static/widgets/eb_widgets.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <section className="relative overflow-hidden py-24 lg:py-32 bg-[#0d0d0d]">
      {" "}
      {/* BACKGROUND DECOR */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#8b2635]/10 blur-3xl" />

        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#2c0f12]/10 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="
    relative
    rounded-[36px]
    border border-white/10
    bg-white/[0.03]
    backdrop-blur-xl
    overflow-hidden
    py-8 md:p-12 lg:p-16
  "
        >
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-reddark/10 rounded-full blur-[120px]" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-[120px]" />
          </div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-reddark via-red-500 to-reddark" />{" "}
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-full h-[1px] bg-white/10" />
          {/* CONTENT */}
          <div className="flex flex-col items-center ">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="flex items-center justify-center gap-4 mb-6"
            >
              <span className="w-12 h-px bg-reddark" />
              <span className="uppercase tracking-[0.1em] text-xs text-white/80 font-medium text-center">
                Eventbrite Workshops
              </span>
              <span className="w-12 h-px bg-reddark" />
            </motion.div>

            <h2
              className="
                text-white
      text-3xl md:text-3xl lg:text-5xl 
          font-[Crimson]
      leading-[0.95]
      uppercase
      max-w-6xl
          mb-6 mx-auto text-center
        "
            >
              {t.main}
            </h2>

            <div
              className="
    mt-0
    overflow-hidden
    rounded-0 lg:rounded-3xl
    border border-white/10
    backdrop-blur-xl
  "
            >
              {/* COVER */}
              <div className="relative h-48 md:h-56 overflow-hidden">
                <img
                  src="/maria/events/cover.jpg"
                  alt="Sunwave Cover"
                  className="w-full h-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              </div>

              <div className="relative px-3 lg:px-7 pb-7">
                <div
                  className="
        absolute
        -top-14
        left-7
        w-28
        h-28
        rounded-full
        overflow-hidden
        border-4
        border-[#121212]
        shadow-2xl
      "
                >
                  <img
                    src="/maria/events/avatar.jpg"
                    alt="Sunwave"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* HEADER */}
                <div className="pt-14 flex items-start justify-between">
                  <div>
                    <h3 className="text-4xl font-[Crimson] text-white">
                      Sunwave
                    </h3>
                  </div>

                  <a
                    href="https://www.eventbrite.co.nz/o/sunwave-121328511812"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
          w-12 h-12
          rounded-2xl
          bg-reddark
          text-white
          flex items-center justify-center
          hover:scale-110
          transition-all
        "
                  >
                    <ArrowUpRight size={18} />
                  </a>
                </div>

                {/* LOCATION TAGS */}
                <div className="flex flex-wrap gap-2 mt-6">
                  {t.locations.map((location) => (
                    <span
                      key={location}
                      className="
      inline-flex items-center gap-2
      px-4 py-2
      rounded-full
      bg-white/[0.04]
      border border-white/10
      backdrop-blur-md
      text-xs tracking-wide
      text-white/80
    "
                    >
                      <MapPin
                        size={12}
                        className="text-reddark fill-reddark/20"
                      />
                      {location}
                    </span>
                  ))}
                </div>

                {/* DESCRIPTION */}
                <div className="mt-8 space-y-4">
                  <p className="text-white/70 leading-relaxed">
                    {t.cardDescription1}
                  </p>

                  <p className="text-white/70 leading-relaxed">
                    {t.cardDescription2}
                  </p>

                  <p className="text-white/70 leading-relaxed">
                    {t.cardDescription3}
                  </p>
                </div>
              </div>
            </div>
            {/* CTA */}
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <CTAButton
                variant="primary"
                icon={PhoneCall}
                text={t.button}
                onClick={() =>
                  window.open("https://sunwave.eventbrite.com/", "_blank")
                }
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LatestWorkshop;
